import { useState } from 'react';
import { formatCurrency } from '@/lib/format.js';
import { toast } from '@/components/ui/toaster.jsx';
import { MEMBERSHIP_WORDS, NOT_ON, MARKET_STATE_WORDS, NOT_SENT, SCENARIO_STATUS_WORDS, STANDING_WORDS, NOT_RECORDED, UNATTRIBUTED, UNATTRIBUTED_REASON_WORDS, word } from './words.js';

// THE SCENARIO BUILDER SHELL (V3 overnight 1, Track C stage C4), READ-ONLY.
// Rendered inside a term block behind its "Scenarios" disclosure. Three
// pieces, all from terms[].scenarios in the payload (docs/v3-account-api.md,
// the "scenario builder" block) and nothing else:
//   - the scenarios as cards side by side; tapping a card picks which one
//     the matrix shows (local state, nothing recorded)
//   - the pilot by aircraft matrix of the picked scenario: rows are the
//     pilots on the term (policy pilots first, then anyone a scenario names),
//     columns the account's aircraft plus any a scenario borrows; a cell is
//     the scenario's own membership word (Named / Excluded) or NOT_ON when
//     the scenario has no row for that pilot on that aircraft
//   - the markets table, one column per scenario: the state word and the
//     premium of each market row, NOT_SENT where a scenario has no row for a
//     carrier, with "Send" and "Skip" as disabled buttons titled NOT_WIRED
//   - the term's unattributed_markets (rows the reader could not place under
//     a scenario, contract rule 6) as one list under the UNATTRIBUTED word:
//     rendered once, never copied under every scenario column
// No door, no write, no endpoint: "+ New scenario" toasts that it lands with
// Phase B. The rater's own doors (send, skip, copy, change coverage, add any
// pilot) are the next stage's work and are not pretended here.

export const NOT_WIRED = 'not wired tonight';
export const NEW_SCENARIO_NOT_WIRED = 'New scenario lands with Phase B';

// Rows: every person on the policy, then anyone a scenario names who is not. Payload order.
export function matrixRows(t) {
  const ids = [];
  for (const pp of t.pilots || []) if (pp.person_id && !ids.includes(pp.person_id)) ids.push(pp.person_id);
  for (const s of t.scenarios || []) for (const sp of s.pilots || []) if (sp.person_id && !ids.includes(sp.person_id)) ids.push(sp.person_id);
  return ids;
}

// Columns: the account's aircraft, then any aircraft the policy or a scenario names that is not the account's.
export function matrixColumns(t, aircraft) {
  const ids = (aircraft || []).map((a) => a.id);
  for (const pp of t.pilots || []) if (pp.aircraft_id && !ids.includes(pp.aircraft_id)) ids.push(pp.aircraft_id);
  for (const s of t.scenarios || []) for (const sp of s.pilots || []) if (sp.aircraft_id && !ids.includes(sp.aircraft_id)) ids.push(sp.aircraft_id);
  return ids;
}

// The cell: the scenario's membership row for this pilot on this aircraft, else NOT_ON.
export function membershipOf(scenario, personId, aircraftId) {
  return (scenario.pilots || []).find((sp) => sp.person_id === personId && sp.aircraft_id === aircraftId) || null;
}

// Market rows: one per carrier slug across every scenario, in order of first appearance.
export function marketRows(scenarios) {
  const rows = [];
  for (const s of scenarios || []) for (const m of s.markets || []) {
    const slug = m.carrier?.slug || m.carrier?.name;
    if (slug && !rows.some((r) => r.slug === slug)) rows.push({ slug, name: m.carrier?.name || slug });
  }
  return rows;
}

function marketOf(scenario, slug) {
  return (scenario.markets || []).find((m) => (m.carrier?.slug || m.carrier?.name) === slug) || null;
}

function tally(s) {
  const ms = s.markets || [];
  const quoted = ms.filter((m) => m.state === 'quoted').length;
  const declined = ms.filter((m) => m.state === 'declined').length;
  return ms.length ? `${ms.length} markets, ${quoted} quoted, ${declined} declined` : 'no markets';
}

const TH = 'px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-text-dim border-b border-border';
const TD = 'px-3 py-2 align-top text-sm border-b border-border';
const OFF = 'inline-flex items-center rounded-md border border-dashed border-border px-3 text-xs text-text-dim opacity-60 cursor-not-allowed';

function PilotCell({ m }) {
  return <span className={`inline-flex min-h-[36px] items-center rounded-md border px-3 text-xs ${m ? (m.membership === 'excluded' ? 'border-danger/60 bg-danger-soft text-danger' : 'border-border-strong bg-border/40 font-medium text-text') : 'border-dashed border-border text-text-dim'}`} data-membership={m ? m.membership : 'none'}>{m ? word(MEMBERSHIP_WORDS, m.membership) : NOT_ON}</span>;
}

function MarketCell({ m, label }) {
  if (!m) {
    return (
      <span className="flex flex-wrap items-center gap-2" data-market="none">
        <span className="text-xs text-text-dim">{NOT_SENT}</span>
        <button type="button" disabled title={NOT_WIRED} aria-label={`Send ${label}, ${NOT_WIRED}`} className={`min-h-[36px] ${OFF}`} data-action="send">Send</button>
        <button type="button" disabled title={NOT_WIRED} aria-label={`Skip ${label}, ${NOT_WIRED}`} className={`min-h-[36px] ${OFF}`} data-action="skip">Skip</button>
      </span>
    );
  }
  return (
    <span className="flex flex-col gap-0.5" data-market={m.state || 'unstated'}>
      <span><span className="font-medium" data-market-state>{word(MARKET_STATE_WORDS, m.state)}</span>{m.premium != null ? <span className="ml-2 font-mono">{formatCurrency(m.premium)}</span> : null}</span>
      {m.declination ? <span className="text-xs text-text-muted">{[...(m.declination.reasons || []), m.declination.detail].filter(Boolean).join(', ') || NOT_RECORDED}</span> : null}
    </span>
  );
}

export default function ScenarioBuilder({ t, people = [], aircraft = [] }) {
  const scenarios = t.scenarios || [];
  const [shownId, setShownId] = useState(scenarios[0]?.id || null);
  const shown = scenarios.find((s) => s.id === shownId) || scenarios[0] || null;
  const person = (id) => people.find((p) => p.id === id) || null;
  const personName = (id) => person(id)?.display_name || 'person not on the account';
  const pilotLine = (id) => {
    const p = person(id)?.pilot;
    if (!p) return 'no PHF';
    // A quote surface: medical is a binding fact and never shows here (David 2026-10-30).
    return p.total_flight_hours != null ? `${p.total_flight_hours} TT` : NOT_RECORDED;
  };
  const standingOf = (id) => (t.pilots || []).find((pp) => pp.person_id === id) || null;
  const aircraftOf = (id) => (aircraft || []).find((a) => a.id === id) || null;
  // One source for a column's registration: the account's aircraft row. A
  // column the policy or a scenario borrows that is not on the account says
  // so; the policy roster's own registration column is not read for it.
  const registrationOf = (id) => aircraftOf(id)?.registration || 'aircraft not on the account';
  const rows = matrixRows(t);
  const cols = matrixColumns(t, aircraft);
  const markets = marketRows(scenarios);
  const newScenario = <button type="button" onClick={() => toast.info(NEW_SCENARIO_NOT_WIRED)} className="inline-flex min-h-[36px] items-center rounded-md border border-border px-3 text-xs text-text hover:bg-border/30" data-new-scenario>+ New scenario</button>;

  return (
    <div className="space-y-3 border-t border-border py-3" data-scenario-builder={t.id}>
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-sm font-semibold text-text">Scenarios, this term <span className="ml-1 font-mono text-xs text-text-dim">{scenarios.length}</span></h3>
        <span className="ml-auto">{newScenario}</span>
      </div>
      {scenarios.length === 0 ? <p className="text-xs italic text-text-dim" data-scenarios="none">No scenarios on this term.</p> : null}

      {scenarios.length ? (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-scenario-cards>
          {scenarios.map((s) => (
            <button type="button" key={s.id} onClick={() => setShownId(s.id)} aria-pressed={shown?.id === s.id} data-picks-scenario={s.id}
              className={`flex min-h-[36px] flex-col gap-0.5 rounded-card border bg-surface px-3 py-2 text-left ${shown?.id === s.id ? 'border-accent' : 'border-border hover:border-border-strong'}`}>
              <span className="flex items-center gap-2"><span className="font-mono text-sm font-semibold">{s.label}</span><span className="ml-auto rounded-pill bg-border/60 px-2 py-0.5 text-[11px] text-text" data-scenario-status>{word(SCENARIO_STATUS_WORDS, s.status)}</span></span>
              <span className="text-xs text-text-muted">{(s.pilots || []).filter((sp) => sp.membership === 'rated').map((sp) => personName(sp.person_id)).join(', ') || 'nobody named'}</span>
              <span className="text-xs text-text-dim">{tally(s)}</span>
            </button>
          ))}
        </div>
      ) : null}

      {shown ? (
        <section className="rounded-card border border-border bg-surface" data-scenario-matrix={shown.id}>
          <h4 className="px-3 pt-2 text-sm font-semibold text-text">{shown.label}: who flies what</h4>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead><tr><th className={TH}>Pilot</th>{cols.map((id) => <th key={id} className={TH}><span className="font-mono normal-case tracking-normal text-text">{registrationOf(id)}</span>{aircraftOf(id) ? <span className="ml-2 normal-case tracking-normal">{[aircraftOf(id).year, aircraftOf(id).make, aircraftOf(id).model].filter(Boolean).join(' ')}</span> : null}</th>)}</tr></thead>
              <tbody>
                {rows.length === 0 ? <tr><td className={TD} colSpan={cols.length + 1}><span className="text-xs italic text-text-dim">No pilots on this term.</span></td></tr> : null}
                {rows.map((pid) => (
                  <tr key={pid} data-matrix-row={pid}>
                    <td className={TD}>
                      <span className="font-medium text-text">{personName(pid)}</span>
                      <span className="block text-xs text-text-muted">{pilotLine(pid)}{standingOf(pid) ? <span className="ml-2" data-standing>{word(STANDING_WORDS, standingOf(pid).standing)}</span> : null}</span>
                    </td>
                    {cols.map((aid) => <td key={aid} className={TD}><PilotCell m={membershipOf(shown, pid, aid)} /></td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {scenarios.length ? (
        <section className="rounded-card border border-border bg-surface" data-scenario-markets>
          <div className="flex flex-wrap items-center gap-3 px-3 pt-2">
            <h4 className="text-sm font-semibold text-text">Markets, side by side</h4>
            {shown ? <button type="button" disabled title={NOT_WIRED} className={`ml-auto min-h-[36px] ${OFF}`} data-action="send-all">Send {shown.label} to markets</button> : null}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead><tr><th className={TH}>Carrier</th>{scenarios.map((s) => <th key={s.id} className={TH}>{s.label}</th>)}</tr></thead>
              <tbody>
                {markets.length === 0 ? <tr><td className={TD} colSpan={scenarios.length + 1}><span className="text-xs italic text-text-dim">No markets on any scenario.</span></td></tr> : null}
                {markets.map((r) => (
                  <tr key={r.slug} data-market-row={r.slug}>
                    <td className={`${TD} font-medium`}>{r.name}</td>
                    {scenarios.map((s) => <td key={s.id} className={TD}><MarketCell m={marketOf(s, r.slug)} label={`${s.label} to ${r.name}`} /></td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="px-3 py-2 text-[11px] text-text-dim" data-not-wired>Send and skip are {NOT_WIRED}: the doors of the rater land with Phase B.</p>
        </section>
      ) : null}

      {(t.unattributed_markets || []).length ? (
        <section className="rounded-card border border-border bg-surface px-3 py-2" data-unattributed-markets>
          <h4 className="text-sm font-semibold text-text">{UNATTRIBUTED} <span className="ml-1 font-mono text-xs text-text-dim">{t.unattributed_markets.length}</span></h4>
          <p className="text-[11px] text-text-dim">Which scenario went to the carrier is not known for these rows; each says why. The state is what the carrier answered on the term.</p>
          <ul className="mt-1">
            {t.unattributed_markets.map((m, i) => (
              <li key={(m.carrier?.slug || m.carrier?.name || 'carrier') + i} className="flex flex-wrap items-center gap-3 border-t border-border py-1.5" data-unattributed-market={m.carrier?.slug || 'unstated'}>
                <span className="font-medium text-text">{m.carrier?.name || m.carrier?.slug || <span className="text-text-dim" data-state-word>{NOT_RECORDED}</span>}</span>
                <MarketCell m={m} label={m.carrier?.name || ''} />
                <span className={`text-[11px] ${m.reason === 'mismatch' ? 'text-warning' : 'text-text-dim'}`} data-unattributed-reason={m.reason || 'unstated'}>{word(UNATTRIBUTED_REASON_WORDS, m.reason)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
